import { request } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  "failed to get monthly ranking": "Не удалось загрузить рейтинг за месяц",
  "failed to get monthly winners": "Не удалось загрузить назначенные награды",
  "failed to assign winners": "Не удалось назначить награды",
  "invalid year": "Некорректный год",
  "invalid month": "Некорректный месяц",
});

export interface MonthlyRankingRow {
  player_id: string;
  player_name: string;
  player_login: string;
  player_avatar_url: string | null;
  group_id: string;
  group_name: string;
  group_color: string;
  monthly_points: number;
}

export type MonthlyRewardStatus = "pending" | "selected" | "given";

// A player_rewards row. Deleted rewards and deactivated players are not listed.
export interface MonthlyReward {
  id: string;
  month: string;
  position: number;
  status: MonthlyRewardStatus;
  player_id: string;
  player_name: string;
  group_id: string;
  group_name: string;
  reward_id: string | null;
  reward_title: string | null;
}

export interface WinnerInput {
  player_id: string;
  group_id: string;
  month: string;
  position: number;
}

export interface AssignWinnersResult {
  ok: boolean;
  assigned: number;
  skipped: number;
}

export interface Winner {
  player_id: string;
  player_name: string;
  group_id: string;
  group_name: string;
  points: number;
  place: number;
}

export const monthlyWinnersApi = {
  ranking: (year: number, month: number) =>
    request<MonthlyRankingRow[] | null>(
      `/api/stats/monthly-ranking?${new URLSearchParams({ year: String(year), month: String(month) })}`,
    ),

  // The endpoint takes no filters and returns every month's rewards.
  rewards: () => request<MonthlyReward[] | null>("/api/stats/monthly-winners"),

  // The server skips a player who already has a reward for that month in any
  // group, including a deleted one.
  assign: (winners: WinnerInput[]) =>
    request<AssignWinnersResult>("/api/player-rewards/assign-winners", {
      method: "POST",
      body: JSON.stringify(winners),
    }),
};

// Same rule as the server's month-end job (scheduler.RankGroupRows): dense
// ranking per group, tied points share a place, top three places, zero
// points never win. Keeping them equal means a manual assignment gives the
// same places the job would have.
export function rankWinners(rows: MonthlyRankingRow[], places = 3): Winner[] {
  const byGroup = new Map<string, MonthlyRankingRow[]>();
  for (const row of rows) {
    byGroup.set(row.group_id, [...(byGroup.get(row.group_id) ?? []), row]);
  }
  const winners: Winner[] = [];
  byGroup.forEach((groupRows) => {
    const sorted = groupRows
      .slice()
      .sort((a, b) => b.monthly_points - a.monthly_points || a.player_name.localeCompare(b.player_name, "ru"));
    let place = 0;
    let previous: number | null = null;
    for (const row of sorted) {
      const points = Number(row.monthly_points);
      if (points <= 0) continue;
      if (points !== previous) place++;
      previous = points;
      if (place > places) break;
      winners.push({
        player_id: row.player_id,
        player_name: row.player_name,
        group_id: row.group_id,
        group_name: row.group_name,
        points,
        place,
      });
    }
  });
  return winners;
}
